import type { PnlHistoryPointDto } from '../../lib/api/endpoints';
import { formatCurrency } from '../../lib/format/currency';

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

interface MonthlyPnlGridProps {
  currency: string;
  points: PnlHistoryPointDto[] | undefined;
  year: number;
  lastSelectableMonth: number;
  selectedMonth: number | null;
  onSelect: (monthIndex: number) => void;
}

function monthKey(year: number, monthIndex: number): string {
  return `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
}

export function MonthlyPnlGrid({
  currency,
  lastSelectableMonth,
  onSelect,
  points,
  selectedMonth,
  year,
}: MonthlyPnlGridProps) {
  const pnlByMonth = new Map((points ?? []).map((point) => [point.date, Number(point.pnl)]));
  const yearTotal = [...pnlByMonth.values()].reduce((sum, value) => sum + value, 0);

  return (
    <section className="rounded border border-border bg-surface p-4">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold">Monthly P&L</h2>
          <p className="text-sm text-text-muted">Select a month to see the trades behind it.</p>
        </div>
        <span className="text-sm tabular-nums text-text-muted">
          {year} total:{' '}
          <span className={pnlTextClass(yearTotal)}>{signedCurrency(yearTotal, currency)}</span>
        </span>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-6">
        {MONTH_LABELS.map((label, index) => {
          const key = monthKey(year, index);
          const pnl = pnlByMonth.get(key);
          const selected = selectedMonth === index;
          const disabled = index > lastSelectableMonth;
          const valueLabel = pnl === undefined ? 'No closed trades' : signedCurrency(pnl, currency);
          return (
            <button
              key={key}
              type="button"
              disabled={disabled}
              aria-pressed={selected}
              aria-label={`${label} ${year}: ${valueLabel}`}
              onClick={() => onSelect(index)}
              className={`min-w-0 rounded border px-3 py-2 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                selected
                  ? 'border-primary bg-surface-raised'
                  : 'border-border hover:border-text-muted hover:bg-surface-raised'
              }`}
            >
              <div className="text-xs uppercase text-text-muted">{label}</div>
              <div
                className={`mt-1 truncate text-sm font-semibold tabular-nums ${
                  pnl === undefined ? 'text-text-muted' : pnlTextClass(pnl)
                }`}
                title={valueLabel}
              >
                {pnl === undefined ? '-' : signedCurrency(pnl, currency)}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function signedCurrency(value: number, currency: string): string {
  const formatted = formatCurrency(value, currency);
  return value > 0 ? `+${formatted}` : formatted;
}

function pnlTextClass(value: number): string {
  if (value > 0) return 'text-success';
  if (value < 0) return 'text-danger';
  return 'text-text';
}
